'use client';

import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Input } from './Input';
import { jobOpenings } from '@/data/data';
import { useTrackButtonClick } from '@/hooks/useTrackButtonClick';


interface ApplyFormProps {
    position: number; // index of default selected position
}

const positions = jobOpenings.map((job) => job.title);

const ApplyForm: React.FC<ApplyFormProps> = ({ position }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        coverLetter: '',
        selectedPosition: positions[position] || positions[0],
        consent: false,
    });

    // Update when prop changes
    useEffect(() => {
        setFormData((prev) => ({
            ...prev,
            selectedPosition: positions[position] || positions[0],
        }));
    }, [position]);

    const [file, setFile] = useState<File | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [status, setStatus] = useState('');

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.[0]) {
            const f = e.target.files[0];
            setSelectedFile(f);
            setFile(f);
        }
    };

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
        const { id, value, type, checked } = e.target as HTMLInputElement;
        setFormData((prev) => ({
            ...prev,
            [id]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!formData.consent) {
            toast.error('You must agree to the data policy.');
            return;
        }
        setIsSubmitting(true);

        try {
            const payload = new FormData();
            payload.append('name', formData.name);
            payload.append('email', formData.email);
            payload.append('phone', formData.phone);
            payload.append('coverLetter', formData.coverLetter);
            payload.append('position', formData.selectedPosition);
            if (file) payload.append('resume', file);

            const res = await fetch('/api/apply', {
                method: 'POST',
                body: payload,
            });

            if (!res.ok) throw new Error('Failed to submit application');
            toast.success('Application submitted!');
            setStatus('Application submitted!');
            setFormData({
                name: '',
                email: '',
                phone: '',
                coverLetter: '',
                selectedPosition: positions[position] || positions[0],
                consent: false,
            });
            setFile(null);
            setSelectedFile(null);
        } catch (err: unknown) {
            toast.error(err instanceof Error ? err.message : 'Unknown error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const trackClick = useTrackButtonClick();

    return (
        <section id="apply" className="w-full h-full">
            <div className='max-w-4xl h-full mx-auto my-20 z-10'>
                <div className="sm:bg-[#1A1A23]/90 text-center sm:rounded-2xl sm:px-16 px-4 sm:py-16 py-4">
                    <div className="w-full h-full mx-auto relative">
                            <h2 className="sectionTitle">Apply for this Position</h2>
                            <p className="text-xl text-gray-400 mb-8">
                                Please fill out the form below to apply.
                            </p>

                            <form className="flex flex-col gap-6" onSubmit={handleSubmit}>
                                {/* Position Dropdown */}
                                <div className="flex flex-col">
                                    <label htmlFor="selectedPosition" className="mb-2 text-base text-yellow-500 text-left">
                                        Position *
                                    </label>
                                    <select
                                        id="selectedPosition"
                                        value={formData.selectedPosition}
                                        onChange={handleChange}
                                        required
                                        className="px-4 py-3 rounded-md border border-[#2a2a38] 
                                    bg-[#1a1a23] text-gray-100 text-sm focus:outline-none 
                                    focus:ring-2 focus:ring-yellow-500/30"
                                    >
                                        {positions.map((pos, i) => (
                                            <option key={i} value={pos}>
                                                {pos}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Name / Email */}
                                <div className="flex flex-col md:flex-row gap-6">
                                    <div className="flex flex-col flex-1">
                                        <label htmlFor="name" className="mb-2 text-base text-yellow-500 text-left">
                                            Full Name *
                                        </label>
                                        <Input
                                            type="text"
                                            id="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            placeholder="Your full name"
                                            required
                                        />
                                    </div>

                                    <div className="flex flex-col flex-1">
                                        <label htmlFor="email" className="mb-2 text-base text-yellow-500 text-left">
                                            Email *
                                        </label>
                                        <Input
                                            type="email"
                                            id="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            placeholder="Your email"
                                            required
                                        />
                                    </div>
                                </div>

                                {/* Phone */}
                                <div className="flex flex-col">
                                    <label htmlFor="phone" className="mb-2 text-base text-yellow-500 text-left">
                                        Phone *
                                    </label>
                                    <Input
                                        type="tel"
                                        id="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        placeholder="Your phone number"
                                        required
                                    />
                                </div>

                                {/* Cover Letter */}
                                <div className="flex flex-col">
                                    <label htmlFor="coverLetter" className="mb-2 text-base text-yellow-500 text-left">
                                        Cover Letter *
                                    </label>
                                    <Input
                                        as="textarea"
                                        id="coverLetter"
                                        value={formData.coverLetter}
                                        onChange={handleChange}
                                        rows={4}
                                        placeholder="Your cover letter"
                                        required
                                    />
                                </div>

                                {/* File Upload */}
                                <div className="flex flex-col">
                                    <label className="mb-2 text-base text-yellow-500 text-left">
                                        Upload CV/Resume
                                    </label>

                                    <div className="flex flex-wrap items-center gap-4">
                                        <input
                                            id="resume"
                                            type="file"
                                            onChange={handleFileChange}
                                            accept=".pdf,.doc,.docx"
                                            className="hidden"
                                        />
                                        <label
                                            htmlFor="resume"
                                            className="cursor-pointer px-4 py-2 rounded-md border border-[#2a2a38] 
                                        bg-[#1a1a23] text-gray-100 text-sm hover:border-yellow-500 
                                        hover:bg-[#242432] transition focus:outline-none focus:ring-2 
                                        focus:ring-yellow-500/30"
                                        >
                                            Choose File
                                        </label>
                                        <p className="text-gray-300 text-sm truncate max-w-[200px]">
                                            {selectedFile ? selectedFile.name : 'No file chosen'}
                                        </p>
                                        <p className="text-gray-500 text-xs mt-1">
                                            ( Allowed types: .pdf, .doc, .docx )
                                        </p>
                                    </div>
                                </div>

                                {/* Consent */}
                                <div className="flex items-center gap-2 mt-4">
                                    <input
                                        type="checkbox"
                                        id="consent"
                                        checked={formData.consent}
                                        onChange={handleChange}
                                        required
                                    />
                                    <label htmlFor="consent" className="text-sm text-gray-300 text-left">
                                        By using this form you agree with the storage and handling of your data by this
                                        website. *
                                    </label>
                                </div>
  
                                <div className="mt-8 flex">
                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        onClick={() => trackClick("Send Message Button Career Page")}
                                        className="inline-flex items-center justify-center text-sm sm:text-base
                                    rounded-md font-semibold px-6 py-3 transition duration-200
                                    disabled:opacity-50 disabled:cursor-not-allowed bg-[#F0C869] 
                                    text-black hover:bg-yellow-400 active:bg-yellow-600 mb-4 
                                    shadow-[0_4px_10px_0_rgba(240,200,105,0.5)] 
                                    hover:shadow-[0_6px_15px_0_rgba(240,200,105,0.7)]"
                                    >
                                        {isSubmitting ? 'Submitting...' : 'Submit Application'}
                                    </button>
                                </div>

                                {status && <p className="mt-4 text-lg text-yellow-500">{status}</p>}
                            </form>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ApplyForm;
